import { AxiosError, AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import api from '../../config/api/api';
import { Exception } from '../models';

export interface ProgressStatus {
  loading: boolean;
  success: boolean;
  error: boolean;
  progress: number;
}

export interface ProgressHttpRequest<S = any, E = Exception> extends ProgressStatus {
  successData: null | S;
  backEndErrors: null | E;
  statusCode: number;
}

export interface UseProgressRequest<S = any, E = Exception> extends ProgressHttpRequest<S, E> {
  fetch: (r2?: AxiosRequestConfig) => void;
  reset: () => void;
  useFetchBy: (condition: any, trigger?: any | 'init') => void;
  useSuccess: (f: () => void) => void;
  useFailure: (f: () => void) => void;
  useLoading: (f: () => void) => void;
  useChange: (f: () => void) => void;
}

const useProgressRequest = <S extends any = any, E extends Exception = Exception>(
  r?: AxiosRequestConfig
): UseProgressRequest<S, E> => {
  const [requestConfig, setRequestConfig] = useState<AxiosRequestConfig>();
  const [status, setStatus] = useState<ProgressHttpRequest<S, E>>({
    backEndErrors: null,
    error: false,
    loading: false,
    success: false,
    successData: null,
    statusCode: 0,
    progress: 0,
  });
  const isActive = useRef<boolean>(false);
  const abortSignal = useRef<AbortController>(new AbortController());

  const rSerialized = JSON.stringify(r);
  useEffect(() => {
    if (r !== undefined) {
      setRequestConfig(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rSerialized]);

  const reset = useCallback(() => {
    setStatus({
      backEndErrors: null,
      error: false,
      loading: false,
      success: false,
      successData: null,
      statusCode: 0,
      progress: 0,
    });
  }, []);

  const fetch = useCallback(
    (r2?: AxiosRequestConfig) => {
      setStatus({
        backEndErrors: null,
        error: false,
        loading: true,
        success: false,
        successData: null,
        statusCode: 0,
        progress: 0,
      });

      const f = async () => {
        isActive.current = true;
        const config = r2 || requestConfig;
        if (config !== undefined) {
          try {
            const response = await api.request({
              ...config,
              signal: abortSignal.current.signal,
              onDownloadProgress: ({ total, loaded }) => {
                if (total) {
                  const percentCompleted = Math.round((loaded * 100) / total);
                  setStatus((prev) => ({ ...prev, progress: percentCompleted }));
                } else {
                  setStatus((prev) => ({ ...prev, progress: 0 }));
                }
              },
            });
            const data = await response?.data;
            setStatus({
              backEndErrors: null,
              error: false,
              loading: false,
              success: true,
              successData: data,
              statusCode: response.status,
              progress: 100,
            });
          } catch (er) {
            const axiosError = (await er) as AxiosError;
            const errorResponse = axiosError.response;
            const responseType = errorResponse?.config.responseType;

            if (axiosError.response?.status === 401) {
              throw axiosError;
            }

            if (responseType === 'blob') {
              const jsonString = await errorResponse?.data.text();
              const errorData = JSON.parse(jsonString);

              const backendError: Exception = {
                status: errorData?.status ?? null,
                data: errorData?.data ?? null,
                message: errorData?.message ?? errorData.title ?? null,
                source: errorData?.source ?? null,
                stackTrace: errorData?.stackTrace ?? null,
              };
              setStatus({
                backEndErrors: (backendError || errorResponse) as E,
                error: true,
                loading: false,
                success: false,
                successData: null,
                statusCode: 0,
                progress: 0,
              });
            } else {
              const backendError: Exception = {
                status: errorResponse?.status ?? null,
                data: errorResponse?.data?.data ?? null,
                message: errorResponse?.data?.message ?? null,
                source: errorResponse?.data?.source ?? null,
                stackTrace: errorResponse?.data?.stackTrace ?? null,
              };
              setStatus({
                backEndErrors: (backendError || errorResponse) as E,
                error: true,
                loading: false,
                success: false,
                successData: null,
                statusCode: 0,
                progress: 0,
              });
            }
          } finally {
            isActive.current = false;
          }
        }
      };

      if (!isActive.current) {
        f();
      }
    },
    [requestConfig]
  );

  const useFetchBy = (condition: any, trigger?: any | 'init') => {
    useEffect(() => {
      const f2 = requestConfig ? () => fetch(requestConfig) : fetch;
      if (condition) f2();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition, trigger]);
  };

  const useSuccess = (f2: () => void) => {
    const statusSuccess = status.success;
    useEffect(() => {
      if (statusSuccess) {
        f2();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusSuccess]);
  };

  const useFailure = (f2: () => void) => {
    const statusError = status.error;
    useEffect(() => {
      if (statusError) {
        f2();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusError]);
  };

  const useLoading = (f2: () => void) => {
    const statusLoading = status.loading;
    useEffect(() => {
      if (statusLoading) {
        f2();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusLoading]);
  };

  const useChange = (f2: () => void) => {
    const statusSerialized = JSON.stringify(status);
    useEffect(() => {
      f2();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusSerialized]);
  };

  return {
    fetch,
    reset,
    useFetchBy,
    useSuccess,
    useFailure,
    useLoading,
    useChange,
    ...status,
  };
};

export default useProgressRequest;
